import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, map, startWith } from 'rxjs/operators';
import { of } from 'rxjs';

import { BaseCarouselComponent } from '../../../base/base-carousel-component';
import { HomeService } from '../home.service';
import { BiblioMapper } from '../../../features/browse-index/mappers/biblio.mapper';
import { SkeletonCardComponent } from '../../../features/browse-index/components/skeleton-card/skeleton-card.component';
import { HomeLibraryCardComponent } from './components/home-library-card/home-library-card.component';
import { HomeBiblioCardComponent } from './components/home-biblio-card/home-biblio-card.component';

/**
 * HomeNew — the Figma "Home Page" design (node 17:3331).
 *
 * Rendered at /home when `environment.homePageVersion === 'new'`. The previous
 * home page lives untouched in `../home.ts`; both share `HomeService`.
 */

/** A `<swiper-container>` custom element once Swiper has initialised it. */
type SwiperElement = HTMLElement & {
  swiper?: {
    slideNext(): void;
    slidePrev(): void;
    slideTo(index: number): void;
    isLocked: boolean;
    isBeginning: boolean;
    isEnd: boolean;
    /** One entry per page; its length is the number of pagination dots. */
    snapGrid: number[];
    snapIndex: number;
    params: { slidesPerGroup?: number };
  };
};

/** A static photo shown in the "معرض الصور" carousel. */
interface GalleryPhoto {
  src: string;
  altKey: string;
}

@Component({
  selector: 'app-home-new',
  imports: [
    CommonModule,
    TranslateModule,
    RouterLink,
    SkeletonCardComponent,
    HomeLibraryCardComponent,
    HomeBiblioCardComponent,
  ],
  templateUrl: './home-new.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrl: './home-new.scss',
})
export class HomeNew extends BaseCarouselComponent implements OnInit {
  private readonly _homeService = inject(HomeService);

  /**
   * Swiper locks itself when every slide already fits, in which case the
   * design's arrows would be inert — hide them rather than ship dead controls.
   */
  /**
   * Records requested for the "أحدث الإضافات" carousel: four pages of three
   * cards, matching the four pagination dots in the design. The original home
   * page keeps the service default of 3.
   */
  private static readonly LATEST_BIBLIOS_COUNT = 12;

  /** Current UI language; follows the translation service without a reload. */
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang || this.translate.defaultLang || 'ar'),
    ),
    { requireSync: true },
  );

  /** Hero banner files for the current language (text is baked into the image). */
  readonly heroSrc = computed(() => {
    const lang = this.currentLang() === 'en' ? 'en' : 'ar';
    return {
      desktop: `images/hero/lib-${lang}-desktop.webp`,
      mobile: `images/hero/lib-${lang}-mobile.webp`,
    };
  });

  readonly latestCarouselLocked = signal(true);
  readonly galleryCarouselLocked = signal(true);

  /**
   * State behind the hand-rolled dots for "أحدث الإضافات". Swiper's own
   * pagination renders inside its shadow root, which cannot join the external
   * flex row the design puts the arrows in — so Swiper stays the engine and
   * these mirror its page state.
   */
  readonly latestPageCount = signal(0);
  readonly latestActivePage = signal(0);

  /**
   * Edge state for the arrows. Defaults disable both, so an arrow is only ever
   * green once Swiper has confirmed there is somewhere to go.
   */
  readonly latestAtStart = signal(true);
  readonly latestAtEnd = signal(true);
  readonly latestPages = computed(() =>
    Array.from({ length: this.latestPageCount() }, (_, index) => index)
  );

  readonly topLibrariesError = signal(false);
  readonly latestBibliosError = signal(false);
  readonly kawLibraryError = signal(false);

  /** Covers used when a library record carries no image of its own. */
  readonly libraryFallbackImages: readonly string[] = [
    'images/custom/home-library-1.png',
    'images/custom/home-library-2.png',
    'images/custom/lib-cover-2.jpg',
  ];

  /**
   * The photo gallery has no catalogue endpoint behind it — these are
   * images shipped with the project. There have to
   * be more of them than `slidesPerView` or Swiper locks the carousel.
   *
   * Deliberately excludes the covers used by `libraryFallbackImages` so the
   * same photograph never appears twice on the page.
   */
  readonly galleryPhotos: readonly GalleryPhoto[] = [
    { src: 'images/custom/home-gallery-2.png', altKey: 'homeNew.galleryPhoto1' },
    { src: 'images/custom/home-gallery-1.png', altKey: 'homeNew.galleryPhoto2' },
    { src: 'images/custom/home-collection-3.jpg', altKey: 'homeNew.galleryPhoto3' },
    { src: 'media/images/quran.jpg', altKey: 'homeNew.galleryPhoto4' },
    { src: 'images/custom/gallery-g1.jpg', altKey: 'homeNew.galleryPhoto5' },
    { src: 'images/custom/gallery-g2.jpg', altKey: 'homeNew.galleryPhoto6' },
    { src: 'images/custom/gallery-g3.jpg', altKey: 'homeNew.galleryPhoto7' },
  ];

  topLibraries$ = this._homeService.getTopLibraries().pipe(
    catchError(() => {
      this.topLibrariesError.set(true);
      return of([]);
    })
  );

  kawLibrary$ = this._homeService.getLibraryKAW().pipe(
    map(kaw => {
      if (kaw && kaw.library_hours && kaw.library_hours.length > 0) {
        kaw.library_hours = this.combineLibraryHours(kaw.library_hours);
      }
      return kaw;
    }),
    catchError(() => {
      this.kawLibraryError.set(true);
      return of(null);
    })
  );

  latestBiblios$ = this._homeService.getLatestBiblios(HomeNew.LATEST_BIBLIOS_COUNT).pipe(
    map(biblios => BiblioMapper.toModelList(biblios)),
    catchError(() => {
      this.latestBibliosError.set(true);
      return of([]);
    })
  );

  override ngOnInit(): void {
    super.ngOnInit();
  }

  syncLatestState(swiperEl: SwiperElement): void {
    const swiper = swiperEl?.swiper;
    this.latestCarouselLocked.set(swiper?.isLocked ?? true);
    this.latestPageCount.set(swiper?.snapGrid?.length ?? 0);
    this.latestActivePage.set(swiper?.snapIndex ?? 0);
    this.latestAtStart.set(swiper?.isBeginning ?? true);
    this.latestAtEnd.set(swiper?.isEnd ?? true);
  }

  goToLatestPage(swiperEl: SwiperElement, page: number): void {
    const swiper = swiperEl?.swiper;
    swiper?.slideTo(page * (swiper.params.slidesPerGroup ?? 1));
  }

  syncGalleryLock(swiperEl: SwiperElement): void {
    this.galleryCarouselLocked.set(swiperEl?.swiper?.isLocked ?? true);
  }

  slideNext(swiperEl: SwiperElement): void {
    swiperEl?.swiper?.slideNext();
  }

  slidePrev(swiperEl: SwiperElement): void {
    swiperEl?.swiper?.slidePrev();
  }

  combineLibraryHours(hours: any[]): any[] {
    if (!hours || hours.length === 0) return [];

    // Sort hours by day (0=Sunday to 6=Saturday)
    const sortedHours = [...hours].sort((a, b) => parseInt(a.day) - parseInt(b.day));

    const combined: any[] = [];
    let currentGroup: any = null;

    for (const hour of sortedHours) {
      if (!currentGroup) {
        currentGroup = { ...hour, start_day: hour.day, end_day: hour.day };
      } else {
        if (hour.open_time === currentGroup.open_time && hour.close_time === currentGroup.close_time && parseInt(hour.day) === parseInt(currentGroup.end_day) + 1) {
          currentGroup.end_day = hour.day;
        } else {
          combined.push(currentGroup);
          currentGroup = { ...hour, start_day: hour.day, end_day: hour.day };
        }
      }
    }
    if (currentGroup) {
      combined.push(currentGroup);
    }

    return combined;
  }

  formatTime(time: string): { h: string, m: string, ampm: string } {
    if (!time) return { h: '', m: '', ampm: '' };
    const [hours, minutes] = time.split(':');
    let h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12;
    h = h ? h : 12;
    return { h: h.toString(), m: minutes, ampm };
  }
}
